// Turns the free-text Duration values chosen in the Rx (drug advice) table into
// concrete next-visit suggestions.
//
// Why the longest course is the recommended default: the follow-up visit exists to
// reassess the patient once the prescribed treatment completes, and the treatment
// completes only when the longest-running medicine finishes. The shorter durations
// still matter — a doctor may want an interim review when a short course (say an
// antibiotic) ends while maintenance medicines continue — so every distinct end
// point is offered, sorted soonest-first, with the full-course one marked out.

const DAYS_PER_UNIT = { days: 1, weeks: 7, months: 30 }

// Parses duration labels like "5 days", "2 Weeks", "1 month", "10d", "3 wk" or a bare
// number (read as days). Returns { value, unit, days } or null for values that are not
// time spans (SOS, To Continue, Stat, ...).
export function parseRxDurationLabel(label) {
  if (typeof label !== 'string') {
    return null
  }
  const text = label.trim().toLowerCase()
  const match = text.match(/^(\d+(?:\.\d+)?)\s*(days?|d|weeks?|wks?|w|months?|mons?|mo|m)?$/)
  if (!match) {
    return null
  }
  const value = Number(match[1])
  if (!Number.isFinite(value) || value <= 0) {
    return null
  }
  const unitToken = match[2] || 'days'
  const unit = unitToken.startsWith('w') ? 'weeks' : unitToken.startsWith('m') ? 'months' : 'days'
  return { value, unit, days: value * DAYS_PER_UNIT[unit] }
}

// "2 weeks", "1 month", "10 days" — the value/unit pair a suggestion would put into
// the next-visit input.
export function formatRxDurationSuggestionLabel(suggestion) {
  if (!suggestion) {
    return ''
  }
  const unitLabel = suggestion.value === 1 ? suggestion.unit.slice(0, -1) : suggestion.unit
  return `${suggestion.value} ${unitLabel}`
}

// drugsAdvice rows -> [{ value, unit, days, drugNames, isFullCourse }] sorted by days
// ascending. Taper chains (a head row plus its taper-continuation rows) count as ONE
// medicine that ends after the SUM of its segment durations. Distinct rows that end on
// the same day merge into one suggestion. The longest suggestion is the full-course end.
export function buildNextVisitSuggestionsFromDrugsAdvice(drugsAdvice) {
  const rows = Array.isArray(drugsAdvice) ? drugsAdvice : []

  const medicineGroups = []
  rows.forEach((row) => {
    if (!row?.drug?.label) {
      return
    }
    if (row?.isTaperContinuation && medicineGroups.length > 0) {
      medicineGroups[medicineGroups.length - 1].rows.push(row)
      return
    }
    medicineGroups.push({ drugName: row.drug.label, rows: [row] })
  })

  const suggestionsByDays = new Map()
  medicineGroups.forEach((group) => {
    const parsedSegments = group.rows
      .map((row) => parseRxDurationLabel(row?.duration?.value ?? row?.duration?.label))
      .filter(Boolean)
    if (!parsedSegments.length) {
      return
    }

    let suggestion
    if (parsedSegments.length === 1) {
      suggestion = parsedSegments[0]
    } else {
      const totalDays = parsedSegments.reduce((acc, segment) => acc + segment.days, 0)
      suggestion = { value: totalDays, unit: 'days', days: totalDays }
    }
    // The next-visit input takes whole numbers; express fractional spans in days
    if (!Number.isInteger(suggestion.value)) {
      const days = Math.round(suggestion.days)
      suggestion = { value: days, unit: 'days', days }
    }
    if (!(suggestion.days > 0)) {
      return
    }

    const existing = suggestionsByDays.get(suggestion.days)
    if (existing) {
      if (!existing.drugNames.includes(group.drugName)) {
        existing.drugNames.push(group.drugName)
      }
    } else {
      suggestionsByDays.set(suggestion.days, { ...suggestion, drugNames: [group.drugName] })
    }
  })

  const suggestions = [...suggestionsByDays.values()].sort((a, b) => a.days - b.days)
  suggestions.forEach((suggestion, index) => {
    suggestion.isFullCourse = index === suggestions.length - 1
  })
  return suggestions
}
